import AccessTimeIcon from "@mui/icons-material/AccessTime";
import GroupsIcon from "@mui/icons-material/Groups";
import MapIcon from "@mui/icons-material/Map";
import PublicIcon from "@mui/icons-material/Public";
import { Box, Button, Card, CardContent, Chip, Divider, Stack, Typography } from "@mui/material";
import { Link } from "react-router-dom";
import {
  storageUrlWithCredentials,
  useStorageUrlWithCredentials,
} from "../../components/imagesPreview/storageUrlWithCredentials";
import type { ListPublicPackagesResponseItem } from "../../publicPackages/publicPackagesApi";
import { CardMediaImg } from "../CardImage";
import { packageImageSx } from "../packageMedia";
import { packageCountries, packageGroupTypes } from "../packageTagsInfo";
import { TransportationIcon, TransportationName } from "../transportation";
import { packageNameWithoutId, userFriendlyPackageId } from "../userFriendltPackageId";

export function PackagesListingCard(props: {
  value: ListPublicPackagesResponseItem;
  offerCount: number;
  onOffersSelected(): void;
  onCalculationSelected(): void;
  onOpenImage(url: string): void;
}) {
  const pkg = props.value;
  const offerCount = props.offerCount;
  const mapThumbnailUrl = useStorageUrlWithCredentials(pkg.mapUrl);

  return (
    <Card
      key={pkg.id.id}
      sx={{
        display: "flex",
        flexDirection: "column",
        borderRadius: 2,
        border: "1px solid rgba(0,0,0,0.05)",
        boxShadow: "0 4px 20px rgba(0,0,0,0.03)",
        transition: "transform 0.2s, box-shadow 0.2s",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: "0 12px 28px rgba(0,0,0,0.08)",
        },
        "&:hover .package-map-overlay": {
          opacity: 1,
        },
      }}
    >
      <Box component={Link} sx={{ position: "relative", cursor: "pointer" }} to={`/public/packages/${pkg.id.id}`}>
        <CardMediaImg
          sx={packageImageSx}
          imageUrl={pkg.images.at(0)?.thumbnail.url}
          alt={pkg.name}
          className="package-base-image"
        />
        {mapThumbnailUrl && (
          <Box
            className="package-map-overlay"
            sx={{
              position: "absolute",
              inset: 0,
              opacity: 0,
              transition: "opacity 0.25s ease",
              bgcolor: "rgba(0,0,0,0.18)",
            }}
          >
            <Box
              component="img"
              src={mapThumbnailUrl}
              alt={`Mapa ${pkg.name}`}
              sx={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "linear-gradient(to top, rgba(0,0,0,0.55), rgba(0,0,0,0.15))",
              }}
            >
              <Button
                variant="contained"
                color="primary"
                disableElevation
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  storageUrlWithCredentials(props.value.mapUrl).then((url) => {
                    if (url) {
                      props.onOpenImage(url);
                    }
                  });
                }}
                sx={{ borderRadius: 2, px: 3, py: 1, fontWeight: "bold" }}
              >
                Pokaż mapę
              </Button>
            </Box>
          </Box>
        )}
        {/* Subtelny badge z numerem pakietu */}
        <Chip
          label={`#${userFriendlyPackageId(pkg)}`}
          size="small"
          sx={{
            position: "absolute",
            top: 12,
            right: 12,
            bgcolor: "rgba(255,255,255,0.9)",
            fontWeight: "bold",
          }}
        />
      </Box>
      <CardContent sx={{ flexGrow: 1, p: 3, display: "flex", flexDirection: "column" }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: "bold",
            lineHeight: 1.3,
            mb: 1,
          }}
        >
          {pkg.name}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
            mb: 3,
          }}
        >
          {packageNameWithoutId(pkg.packageName)}
        </Typography>

        {/* Tagi zastępujące długie listy */}
        <Stack spacing={2} sx={{ mb: 3, mt: "auto" }}>
          <Box>
            <Stack
              direction="row"
              spacing={1}
              sx={{
                alignItems: "center",
                mb: 1,
              }}
            >
              <PublicIcon fontSize="small" sx={{ color: "text.disabled" }} />
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                  textTransform: "uppercase",
                  fontWeight: "bold",
                }}
              >
                Kraje
              </Typography>
            </Stack>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {packageCountries(pkg).map((c) => (
                <Chip
                  key={c}
                  label={c}
                  size="small"
                  sx={{ bgcolor: "rgba(13, 110, 253, 0.08)", color: "primary.main", fontWeight: 500 }}
                />
              ))}
            </Box>
          </Box>

          <Box>
            <Stack
              direction="row"
              spacing={1}
              sx={{
                alignItems: "center",
                mb: 1,
              }}
            >
              <GroupsIcon fontSize="small" sx={{ color: "text.disabled" }} />
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                  textTransform: "uppercase",
                  fontWeight: "bold",
                }}
              >
                Dla kogo
              </Typography>
            </Stack>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {packageGroupTypes(pkg).map((g) => (
                <Chip key={g} label={g} size="small" variant="outlined" sx={{ borderColor: "rgba(0,0,0,0.12)" }} />
              ))}
            </Box>
          </Box>
        </Stack>

        <Divider sx={{ mb: 2 }} />

        {/* Parametry i link do mapy */}
        <Stack
          direction="row"
          sx={{
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Stack
            direction="row"
            spacing={2}
            sx={{
              color: "text.secondary",
            }}
          >
            <Stack
              direction="row"
              spacing={0.5}
              sx={{
                alignItems: "center",
              }}
            >
              <AccessTimeIcon fontSize="small" />
              <Typography
                variant="body2"
                sx={{
                  fontWeight: "500",
                }}
              >
                {pkg.numberOfDays} dni
              </Typography>
            </Stack>
            <Stack
              direction="row"
              spacing={0.5}
              sx={{
                alignItems: "center",
              }}
            >
              <TransportationIcon forPackage={pkg} />
              <Typography
                variant="body2"
                sx={{
                  fontWeight: "500",
                }}
              >
                <TransportationName forPackage={pkg} />
              </Typography>
            </Stack>
          </Stack>

          {pkg.mapUrl && (
            <Button
              size="small"
              startIcon={<MapIcon />}
              sx={{ textTransform: "none", color: "text.secondary" }}
              onClick={() =>
                storageUrlWithCredentials(pkg.mapUrl).then((url) => {
                  if (url) {
                    props.onOpenImage(url);
                  }
                })
              }
            >
              Mapa
            </Button>
          )}
        </Stack>

        <Stack spacing={1}>
          <Button
            variant="contained"
            color="primary"
            fullWidth
            disableElevation
            sx={{ py: 1 }}
            component={Link}
            to={`/public/packages/${pkg.id.id}`}
          >
            Zobacz szczegóły
          </Button>
          {offerCount > 0 ? (
            <Button
              variant="contained"
              color="primary"
              fullWidth
              disableElevation
              sx={{ py: 1 }}
              onClick={() => {
                props.onOffersSelected();
              }}
            >
              Gotowe terminy w e-commerce {offerCount}
            </Button>
          ) : (
            <Button
              variant="contained"
              color="primary"
              fullWidth
              disableElevation
              sx={{ py: 1 }}
              onClick={() => {
                props.onCalculationSelected();
              }}
            >
              Wyceń pakiet
            </Button>
          )}
          {/* <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              fullWidth
              size="small"
              sx={{ color: "text.secondary", borderColor: "rgba(0,0,0,0.15)", borderRadius: 2 }}
              component={Link}
              to={`/calculations/new?fromPackage=${pkg.id.id}`}
            >
              Wyceń
            </Button>
            <Button
              variant="outlined"
              fullWidth
              size="small"
              sx={{ color: "text.secondary", borderColor: "rgba(0,0,0,0.15)", borderRadius: 2 }}
              component={Link}
              to={`/e-commerce?query=${userFriendlyPackageId(pkg)}`}
            >
              Sklep
            </Button>
          </Stack> */}
        </Stack>
      </CardContent>
    </Card>
  );
}
